import { useState, useEffect, useRef, useCallback } from 'react';
import { userController } from '../../controllers/userController.js';
import { ROLE_LABELS, ROLE_COLORS } from '../../models/User.js';
import {
  CheckCircle, ChevronDown, Search, X, Loader,
  AlertCircle, Eye, EyeOff, FileText, Image,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────
const ROLE_ICONS = {
  admin: '🛡️', super_distributor: '🌐', distributor: '📦',
  subdistributor: '🔗', retailer: '🏪', employee: '👤',
};

// Who can create whom  (creator role → creatable roles)
const CAN_CREATE = {
  super_admin:       ['admin','super_distributor','distributor','subdistributor','retailer','employee'],
  admin:             ['super_distributor','distributor','subdistributor','retailer','employee'],
  super_distributor: ['distributor','subdistributor','retailer','employee'],
  distributor:       ['subdistributor','retailer','employee'],
  subdistributor:    ['retailer','employee'],
  retailer:          ['employee'],
};

/**
 * Hierarchy steps for each role.
 * Each step: { label, role, required, fetchFrom }
 *   fetchFrom = which previously-selected id to use when fetching THIS step's options
 *   required  = false means "optional — can skip"
 */
const HIERARCHY_STEPS = {
  // admin → no chain needed (super_admin is creator, admin has no company parent)
  admin: [],

  // super_distributor → must pick an Admin parent
  super_distributor: [
    { label: 'Admin (Parent)', role: 'admin', required: true, fetchFrom: null },
  ],

  // distributor → pick Admin → then pick Super Distributor under that admin
  distributor: [
    { label: 'Admin',             role: 'admin',             required: true,  fetchFrom: null },
    { label: 'Super Distributor', role: 'super_distributor', required: true,  fetchFrom: 'admin' },
  ],

  // subdistributor → optional level
  // pick Admin → Super Dist → Distributor (PARENT)
  subdistributor: [
    { label: 'Admin',             role: 'admin',             required: true,  fetchFrom: null },
    { label: 'Super Distributor', role: 'super_distributor', required: true,  fetchFrom: 'admin' },
    { label: 'Distributor',       role: 'distributor',       required: true,  fetchFrom: 'super_distributor' },
  ],

  // retailer → Admin → SD → Dist → optional SubDist → PARENT = last selected
  retailer: [
    { label: 'Admin',             role: 'admin',             required: true,  fetchFrom: null },
    { label: 'Super Distributor', role: 'super_distributor', required: true,  fetchFrom: 'admin' },
    { label: 'Distributor',       role: 'distributor',       required: true,  fetchFrom: 'super_distributor' },
    { label: 'Sub Distributor',   role: 'subdistributor',    required: false, fetchFrom: 'distributor' },
  ],

  // ✅ FIXED: employee → EVERY step ab optional hai.
  // - Kuch bhi select na kare → employee seedha CREATOR ke niche ban jayega
  //   (backend createMember mein: `parentId || creator.id`)
  // - Kisi bhi ek level tak chain select kare aur ruk jaye → wahi parent banega
  //   (jaise sirf Retailer select kiya to us Retailer ka employee banega)
  employee: [
    { label: 'Admin',             role: 'admin',             required: false, fetchFrom: null },
    { label: 'Super Distributor', role: 'super_distributor', required: false, fetchFrom: 'admin' },
    { label: 'Distributor',       role: 'distributor',       required: false, fetchFrom: 'super_distributor' },
    { label: 'Sub Distributor',   role: 'subdistributor',    required: false, fetchFrom: 'distributor' },
    { label: 'Retailer',          role: 'retailer',          required: false, fetchFrom: 'subdistributor' },
  ],
};

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', phone: '',
  password: '', confirmPassword: '',
  role: 'retailer',
  commissionRate: 0,
  address: { city: '', state: '' },
  avatar: null,
  document: null,
};

// ─────────────────────────────────────────────────────────────────
// STYLES  (all inline so no CSS file needed)
// ─────────────────────────────────────────────────────────────────
const S = {
  page:    { padding: 0, fontFamily: "'DM Sans',system-ui,sans-serif", color: '#0f172a' },
  wrap:    { maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 18 },

  card:    { background: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,.07),0 4px 16px rgba(0,0,0,.05)', overflow: 'visible' },
  cHead:   { padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 },
  cBody:   { padding: '22px 24px' },
  cFoot:   { padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center' },

  stepBadge: (on) => ({
    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
    background: on ? '#2563eb' : '#e2e8f0', color: on ? '#fff' : '#94a3b8',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700,
  }),

  cardTitle: { fontWeight: 700, fontSize: 14, color: '#0f172a' },
  cardSub:   { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  label:     { fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 6 },
  required:  { color: '#ef4444', marginLeft: 2 },

  input: (focused) => ({
    width: '100%', padding: '10px 13px', boxSizing: 'border-box',
    border: `1.5px solid ${focused ? '#3b82f6' : '#e2e8f0'}`,
    borderRadius: 10, fontSize: 14, color: '#0f172a', outline: 'none',
    background: focused ? '#fff' : '#fafafa', transition: 'all .15s',
  }),

  btnPrimary: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 22px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSecond:  { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', background: '#fff', color: '#475569', border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer' },

  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 },
  fg:    { display: 'flex', flexDirection: 'column' },

  success: { display: 'flex', alignItems: 'center', gap: 10, background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 12, padding: '14px 18px', color: '#065f46', fontSize: 14, fontWeight: 500, marginBottom: 18 },
  errBox:  { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 18 },
  errItem: { color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 },
};

// ─────────────────────────────────────────────────────────────────
// SINGLE SEARCHABLE DROPDOWN  (portal-style, fixed position)
// ─────────────────────────────────────────────────────────────────
function Dropdown({ label, required, value, onChange, options, loading, disabled, placeholder }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos]       = useState({ top: 0, left: 0, width: 0 });
  const trigRef = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find(o => o.id === value || o._id === value);

  const openDropdown = () => {
    if (disabled || loading) return;
    const rect = trigRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      const menuH = Math.min(280, options.length * 52 + 60);
      const openUp = spaceBelow < menuH + 16 && rect.top > menuH + 16;
      setPos({
        left:  rect.left + window.scrollX,
        width: rect.width,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top - window.scrollY, top: 'auto' }
          : { top: rect.bottom + window.scrollY + 6, bottom: 'auto' }),
      });
    }
    setOpen(true);
    setSearch('');
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!trigRef.current?.contains(e.target) && !menuRef.current?.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = options.filter(o => {
    const q = search.toLowerCase();
    return !q
      || o.fullName?.toLowerCase().includes(q)
      || o.firstName?.toLowerCase().includes(q)
      || o.email?.toLowerCase().includes(q)
      || o.customId?.toLowerCase().includes(q);
  });

  const color = selected ? (ROLE_COLORS[selected.role] || '#64748b') : '#64748b';

  return (
    <div style={S.fg}>
      {label && <label style={S.label}>{label}{required && <span style={S.required}>*</span>}</label>}

      <div
        ref={trigRef}
        onClick={openDropdown}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 13px', borderRadius: 10, fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer',
          border: `1.5px solid ${open ? '#3b82f6' : '#e2e8f0'}`,
          background: disabled ? '#f8fafc' : open ? '#fff' : '#fafafa',
          opacity: disabled ? .6 : 1, transition: 'all .15s', boxSizing: 'border-box',
          minHeight: 43,
        }}
      >
        {selected ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: 1, minWidth: 0 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: `${color}20`, border: `2px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color, flexShrink: 0 }}>
              {(selected.firstName?.[0] || '?').toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selected.firstName} {selected.lastName}
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{ROLE_LABELS[selected.role]} · {selected.customId}</div>
            </div>
          </div>
        ) : (
          <span style={{ color: '#94a3b8', fontSize: 14 }}>
            {loading ? 'Loading…' : disabled ? 'Not required' : (placeholder || 'Select…')}
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {selected && !disabled && (
            <span onMouseDown={e => { e.stopPropagation(); onChange(''); }} style={{ cursor: 'pointer', display: 'flex', color: '#94a3b8' }}>
              <X size={14} />
            </span>
          )}
          {loading
            ? <Loader size={14} color="#94a3b8" style={{ animation: 'spin 1s linear infinite' }} />
            : <ChevronDown size={15} color="#94a3b8" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
          }
        </div>
      </div>

      {open && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top:    pos.top    !== 'auto' ? pos.top    : undefined,
            bottom: pos.bottom !== 'auto' ? pos.bottom : undefined,
            left:   pos.left,
            width:  pos.width,
            zIndex: 99999,
            background: '#fff',
            border: '1.5px solid #e2e8f0',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,.15)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 300,
          }}
        >
          <div style={{ padding: '9px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
            <Search size={13} color="#94a3b8" />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, ID…"
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#0f172a', background: 'transparent' }}
            />
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                {search ? 'No results found' : 'No users available'}
              </div>
            ) : filtered.map(u => {
              const c   = ROLE_COLORS[u.role] || '#64748b';
              const sel = value === (u.id || u._id);
              return (
                <div
                  key={u.id || u._id}
                  onMouseDown={() => { onChange(u.id || u._id); setOpen(false); }}
                  style={{
                    padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    background: sel ? '#eff6ff' : 'transparent',
                  }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: `${c}20`, border: `2px solid ${c}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: c, flexShrink: 0 }}>
                    {(u.firstName?.[0] || '?').toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {u.firstName} {u.lastName}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{u.email} · {u.customId}</div>
                  </div>
                  {sel && <CheckCircle size={14} color="#2563eb" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FILE UPLOAD WIDGET
// ─────────────────────────────────────────────────────────────────
function FileUpload({ label, accept, icon: Icon, value, onChange, hint }) {
  const ref     = useRef(null);
  const [drag, setDrag] = useState(false);
  const preview = value && value.type?.startsWith('image/') ? URL.createObjectURL(value) : null;

  return (
    <div style={S.fg}>
      <label style={S.label}>{label}</label>
      <div
        onClick={() => ref.current?.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onChange(f); }}
        style={{
          border: `2px dashed ${drag ? '#3b82f6' : value ? '#10b981' : '#cbd5e1'}`,
          borderRadius: 12, padding: '18px 14px', textAlign: 'center', cursor: 'pointer',
          background: drag ? '#eff6ff' : value ? '#f0fdf4' : '#fafafa',
          transition: 'all .2s', position: 'relative',
        }}
      >
        {preview ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <img src={preview} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '3px solid #10b981' }} />
            <span style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>{value.name}</span>
          </div>
        ) : value ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <FileText size={28} color="#10b981" />
            <span style={{ fontSize: 12, color: '#10b981', fontWeight: 600 }}>{value.name}</span>
            <span style={{ fontSize: 11, color: '#64748b' }}>{(value.size / 1024).toFixed(1)} KB</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <Icon size={26} color="#94a3b8" />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Click or drag & drop</span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>{hint}</span>
          </div>
        )}
        <input ref={ref} type="file" accept={accept} style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if (f) onChange(f); }} />
      </div>
      {value && (
        <button type="button" onClick={() => onChange(null)} style={{ alignSelf: 'flex-start', marginTop: 4, fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          ✕ Remove
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// FOCUSED INPUT  (handles focus border state internally)
// ─────────────────────────────────────────────────────────────────
function Input({ label, required: req, type = 'text', ...rest }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={S.fg}>
      {label && <label style={S.label}>{label}{req && <span style={S.required}>*</span>}</label>}
      <input type={type} style={S.input(focused)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} {...rest} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN ADD USER PAGE
// ─────────────────────────────────────────────────────────────────
export function AddUserPage() {
  const [form, setForm]         = useState(EMPTY_FORM);
  const [errors, setErrors]     = useState([]);
  const [submitting, setSub]    = useState(false);
  const [success, setSuccess]   = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConf, setShowConf] = useState(false);

  const [chain, setChain]   = useState({});
  const [opts, setOpts]     = useState({});
  const [loads, setLoads]   = useState({});

  const currentRole = userController.getCurrentRole?.() || 'super_admin';
  const creatableRoles = CAN_CREATE[currentRole] || [];

  const handleRoleChange = (role) => {
    setForm(p => ({ ...p, role }));
    setChain({});
    setOpts({});
    setErrors([]);
    setSuccess(false);
  };

  const fetchStep = useCallback(async (stepRole, parentId) => {
    setLoads(p => ({ ...p, [stepRole]: true }));
    try {
      const filters = parentId ? { parentId } : {};
      const res = await userController.getByRole(stepRole, { filters, limit: 200 });
      setOpts(p => ({ ...p, [stepRole]: res.success ? res.data : [] }));
    } catch {
      setOpts(p => ({ ...p, [stepRole]: [] }));
    }
    setLoads(p => ({ ...p, [stepRole]: false }));
  }, []);

  const steps = HIERARCHY_STEPS[form.role] || [];

  const handleChainSelect = (stepRole, id, stepIndex) => {
    const newChain = { ...chain, [stepRole]: id };
    steps.slice(stepIndex + 1).forEach(s => { delete newChain[s.role]; });
    setChain(newChain);

    const newOpts = { ...opts };
    steps.slice(stepIndex + 1).forEach(s => { delete newOpts[s.role]; });
    setOpts(newOpts);

    const nextStep = steps[stepIndex + 1];
    if (nextStep && id) fetchStep(nextStep.role, id);
  };

  useEffect(() => {
    if (steps.length === 0) return;
    const first = steps[0];
    fetchStep(first.role, null);
  }, [form.role]);

  // Determine final parentId from chain
  // Walk backward from the last step — first non-empty value found = parent.
  // Agar kuch bhi select nahi hua (poori chain empty) → null return hoga,
  // aur backend createMember mein `parentId || creator.id` se creator
  // khud parent ban jayega — isi tarah "khud ka employee create" possible hai.
  const getParentId = () => {
    if (steps.length === 0) return null;
    for (let i = steps.length - 1; i >= 0; i--) {
      const v = chain[steps[i].role];
      if (v) return v;
    }
    return null;
  };

  const validate = () => {
    const errs = [];
    if (!form.firstName.trim()) errs.push('First name is required');
    if (!form.lastName.trim())  errs.push('Last name is required');
    if (!form.email.trim())     errs.push('Email is required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.push('Invalid email address');
    if (!form.phone.trim())     errs.push('Phone is required');
    else if (!/^[0-9]{10}$/.test(form.phone)) errs.push('Phone must be 10 digits');
    if (!form.password)         errs.push('Password is required');
    else if (form.password.length < 8) errs.push('Password must be at least 8 characters');
    if (form.password !== form.confirmPassword) errs.push('Passwords do not match');
    // ✅ Employee ke liye ab koi step required nahi hai (sabhi required:false),
    // isliye ye loop employee role ke liye kabhi error push nahi karega —
    // user chahe to poora skip kar sakta hai.
    steps.forEach(s => {
      if (s.required && !chain[s.role]) errs.push(`Please select a ${s.label}`);
    });
    return errs;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (errs.length) { setErrors(errs); return; }

    setSub(true);
    setErrors([]);

    const parentId = getParentId();

    const fd = new FormData();
    fd.append('firstName',      form.firstName.trim());
    fd.append('lastName',       form.lastName.trim());
    fd.append('email',          form.email.trim().toLowerCase());
    fd.append('phone',          form.phone.trim());
    fd.append('password',       form.password);
    fd.append('role',           form.role);
    fd.append('commissionRate', form.commissionRate);
    if (form.address?.city)  fd.append('address[city]',  form.address.city);
    if (form.address?.state) fd.append('address[state]', form.address.state);
    if (parentId)            fd.append('parentId', parentId); // empty ho to append hi nahi hota → backend default creator lega
    if (form.avatar)         fd.append('avatar',   form.avatar);
    if (form.document)       fd.append('document', form.document);

    const res = await userController.create(fd);
    if (res.success) {
      setForm(EMPTY_FORM);
      setChain({});
      setOpts({});
      setSuccess(true);
    } else {
      setErrors(res.errors?.length ? res.errors : [res.message || 'Failed to create user']);
    }
    setSub(false);
  };

  const pwStrength = form.password.length >= 12 ? { label: 'Strong', color: '#10b981', w: '100%' }
    : form.password.length >= 8  ? { label: 'Good',   color: '#f59e0b', w: '60%'  }
    : form.password.length > 0   ? { label: 'Weak',   color: '#ef4444', w: '30%'  }
    : null;

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, letterSpacing: '-0.5px' }}>Add New User</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Create a user within your hierarchy</p>
      </div>

      {success && (
        <div style={S.success}>
          <CheckCircle size={18} /> User created successfully! They will receive a verification email.
        </div>
      )}

      <div style={S.wrap}>

        {/* ── STEP 1: Role ────────────────────────────────────── */}
        <div style={S.card}>
          <div style={S.cHead}>
            <div style={S.stepBadge(true)}>1</div>
            <div>
              <div style={S.cardTitle}>Select Role</div>
              <div style={S.cardSub}>Choose the role this user will have in the hierarchy</div>
            </div>
          </div>
          <div style={S.cBody}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(115px,1fr))', gap: 10 }}>
              {creatableRoles.map(r => {
                const active = form.role === r;
                const color  = ROLE_COLORS[r] || '#64748b';
                return (
                  <div
                    key={r}
                    onClick={() => handleRoleChange(r)}
                    style={{
                      padding: '13px 8px', borderRadius: 12, cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${active ? color : '#e2e8f0'}`,
                      background: active ? `${color}12` : '#fafafa',
                      transition: 'all .18s', userSelect: 'none',
                    }}
                  >
                    <div style={{ fontSize: 22, marginBottom: 5 }}>{ROLE_ICONS[r]}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: '.03em' }}>{ROLE_LABELS[r]}</div>
                    {active && <CheckCircle size={13} color={color} style={{ marginTop: 5 }} />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── STEP 2: Hierarchy Chain ─────────────────────────── */}
        {steps.length > 0 && (
          <div style={S.card}>
            <div style={S.cHead}>
              <div style={S.stepBadge(true)}>2</div>
              <div>
                <div style={S.cardTitle}>Select Hierarchy</div>
                <div style={S.cardSub}>
                  {form.role === 'employee'
                    ? 'Fully optional — skip everything to create under yourself, or pick a chain to place this employee under someone specific.'
                    : 'Pick the chain — the last selected user becomes the parent. Steps marked optional can be skipped.'}
                </div>
              </div>
            </div>
            <div style={S.cBody}>
              {/* Visual chain breadcrumb */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginBottom: 20 }}>
                {steps.map((s, i) => {
                  const selected = chain[s.role];
                  const color    = ROLE_COLORS[s.role] || '#64748b';
                  return (
                    <div key={s.role} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {i > 0 && <span style={{ color: '#cbd5e1', fontSize: 12 }}>→</span>}
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: selected ? `${color}15` : '#f1f5f9',
                        color:      selected ? color : '#94a3b8',
                        border:     `1px solid ${selected ? `${color}40` : '#e2e8f0'}`,
                      }}>
                        {ROLE_ICONS[s.role]} {s.label}
                        {!s.required && <span style={{ opacity: .6, fontWeight: 400 }}> (opt)</span>}
                        {selected && ' ✓'}
                      </span>
                    </div>
                  );
                })}
                <span style={{ color: '#cbd5e1', fontSize: 12 }}>→</span>
                <span style={{
                  padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                  background: `${ROLE_COLORS[form.role] || '#64748b'}15`,
                  color:       ROLE_COLORS[form.role] || '#64748b',
                  border:      `1.5px solid ${ROLE_COLORS[form.role] || '#64748b'}`,
                }}>
                  {ROLE_ICONS[form.role]} {ROLE_LABELS[form.role]} ← new
                </span>
              </div>

              {/* Dropdowns for each step */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {steps.map((s, i) => {
                  // ✅ FIXED: pehle sirf "required" prior steps ka dhyaan rakhta tha,
                  // isliye jab sab optional (employee) hote the, sab dropdowns ek
                  // saath enable ho jaate the aur chain (fetchFrom) tootjaata tha.
                  // Ab: step 0 hamesha enabled hai; baaki step[i] tabhi enabled
                  // hoga jab step[i-1] mein koi value select ho chuki ho —
                  // required flag se koi farak nahi padta, isse sahi order
                  // maintain hota hai chahe step optional ho ya required.
                  const enabled = i === 0 || !!chain[steps[i - 1].role];
                  const stepOpts = opts[s.role] || [];

                  return (
                    <Dropdown
                      key={s.role}
                      label={s.label}
                      required={s.required}
                      value={chain[s.role] || ''}
                      onChange={id => handleChainSelect(s.role, id, i)}
                      options={enabled ? stepOpts : []}
                      loading={!!loads[s.role]}
                      disabled={!enabled}
                      placeholder={enabled ? `Select ${s.label}… (optional)` : `Select ${steps[i-1]?.label || 'previous'} first`}
                    />
                  );
                })}
              </div>

              {/* Show resolved parent */}
              {getParentId() ? (
                <div style={{ marginTop: 16, padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #86efac', fontSize: 13, color: '#065f46', fontWeight: 500 }}>
                  ✓ Parent ID resolved: <code style={{ background: '#dcfce7', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>{getParentId()}</code>
                </div>
              ) : form.role === 'employee' ? (
                <div style={{ marginTop: 16, padding: '10px 14px', background: '#eff6ff', borderRadius: 10, border: '1px solid #93c5fd', fontSize: 13, color: '#1e40af', fontWeight: 500 }}>
                  ℹ️ Nothing selected — this employee will be created directly under you.
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* ── STEP 3: Details ──────────────────────────────────── */}
        <div style={S.card}>
          <div style={S.cHead}>
            <div style={S.stepBadge(true)}>{steps.length > 0 ? 3 : 2}</div>
            <div>
              <div style={S.cardTitle}>User Details</div>
              <div style={S.cardSub}>Personal information, credentials and uploads</div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={S.cBody}>
              {errors.length > 0 && (
                <div style={S.errBox}>
                  {errors.map((e, i) => (
                    <div key={i} style={S.errItem}><AlertCircle size={13} />{e}</div>
                  ))}
                </div>
              )}

              {/* Personal info */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 14 }}>Personal Information</div>
                <div style={{ ...S.grid2, marginBottom: 14 }}>
                  <Input label="First Name" required value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} placeholder="Rahul" />
                  <Input label="Last Name"  required value={form.lastName}  onChange={e => setForm(p => ({ ...p, lastName:  e.target.value }))} placeholder="Sharma" />
                  <Input label="Email"      required type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="rahul@example.com" />
                  <Input label="Phone"      required type="tel"   value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="9876543210" maxLength={10} />
                  <Input label="Commission Rate (%)" type="number" value={form.commissionRate} onChange={e => setForm(p => ({ ...p, commissionRate: e.target.value }))} min="0" max="100" placeholder="0" />
                  <Input label="City" value={form.address?.city || ''} onChange={e => setForm(p => ({ ...p, address: { ...p.address, city: e.target.value } }))} placeholder="Mumbai" />
                </div>
              </div>

              {/* Credentials */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 14 }}>Login Credentials</div>
                <div style={S.grid2}>
                  <div style={S.fg}>
                    <label style={S.label}>Password<span style={S.required}>*</span></label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPass ? 'text' : 'password'}
                        value={form.password}
                        onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                        placeholder="Min. 8 characters"
                        style={{ ...S.input(false), paddingRight: 40 }}
                      />
                      <button type="button" onClick={() => setShowPass(p => !p)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}>
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {pwStrength && (
                      <>
                        <div style={{ height: 3, borderRadius: 2, background: '#f1f5f9', marginTop: 5, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: pwStrength.w, background: pwStrength.color, borderRadius: 2, transition: 'all .3s' }} />
                        </div>
                        <span style={{ fontSize: 10, color: pwStrength.color, marginTop: 2 }}>{pwStrength.label}</span>
                      </>
                    )}
                  </div>

                  <div style={S.fg}>
                    <label style={S.label}>Confirm Password<span style={S.required}>*</span></label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConf ? 'text' : 'password'}
                        value={form.confirmPassword}
                        onChange={e => setForm(p => ({ ...p, confirmPassword: e.target.value }))}
                        placeholder="Repeat password"
                        style={{ ...S.input(false), paddingRight: 40 }}
                      />
                      <button type="button" onClick={() => setShowConf(p => !p)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}>
                        {showConf ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {form.confirmPassword && (
                      <span style={{ fontSize: 10, marginTop: 4, color: form.password === form.confirmPassword ? '#10b981' : '#ef4444' }}>
                        {form.password === form.confirmPassword ? '✓ Passwords match' : '✗ Do not match'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Uploads */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 14 }}>Profile & Documents</div>
                <div style={S.grid2}>
                  <FileUpload
                    label="Profile Photo"
                    accept="image/*"
                    icon={Image}
                    value={form.avatar}
                    onChange={f => setForm(p => ({ ...p, avatar: f }))}
                    hint="JPG, PNG, WebP · Max 5 MB"
                  />
                  <FileUpload
                    label="ID Document"
                    accept=".pdf,.jpg,.jpeg,.png"
                    icon={FileText}
                    value={form.document}
                    onChange={f => setForm(p => ({ ...p, document: f }))}
                    hint="Aadhaar, PAN, Passport · PDF or Image"
                  />
                </div>
              </div>
            </div>

            <div style={S.cFoot}>
              <button type="button" style={S.btnSecond} onClick={() => { setForm(EMPTY_FORM); setChain({}); setOpts({}); setErrors([]); setSuccess(false); }}>
                Reset
              </button>
              <button type="submit" style={{ ...S.btnPrimary, opacity: submitting ? .7 : 1 }} disabled={submitting}>
                {submitting
                  ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Creating…</>
                  : <><CheckCircle size={15} /> Create User</>}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// BULK IMPORT PAGE  (unchanged)
// ─────────────────────────────────────────────────────────────────
export function BulkImportPage() {
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult]       = useState(null);
  const [drag, setDrag]           = useState(false);

  const handleFile = e => {
    const f = e.target.files?.[0] || e;
    if (!f) return;
    setFile(f); setResult(null);
    const reader = new FileReader();
    reader.onload = ev => {
      const lines   = ev.target.result.split('\n').filter(Boolean);
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const rows    = lines.slice(1, 6).map(line => {
        const vals = line.split(',').map(v => v.replace(/"/g, '').trim());
        return Object.fromEntries(headers.map((h, i) => [h, vals[i]]));
      });
      setPreview(rows);
    };
    reader.readAsText(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    await new Promise(r => setTimeout(r, 1500));
    setResult({ success: 42, failed: 3, total: 45 });
    setImporting(false);
  };

  return (
    <div style={S.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, letterSpacing: '-0.5px' }}>Bulk Import Users</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Upload a CSV file to create multiple users at once</p>
      </div>
      <div style={{ ...S.wrap }}>
        <div style={S.card}>
          <div style={S.cHead}><div style={S.stepBadge(true)}>1</div><div><div style={S.cardTitle}>Download Template</div><div style={S.cardSub}>Use our CSV template — do not change column headers</div></div></div>
          <div style={S.cBody}>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>Required fields: <strong>firstName, lastName, email, phone, role, commissionRate</strong></p>
            <button style={S.btnSecond} onClick={() => {
              const csv = `"firstName","lastName","email","phone","role","commissionRate"\n"Rahul","Sharma","rahul@example.com","9876543210","retailer","5"`;
              const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })), download: 'user_import_template.csv' });
              a.click();
            }}>⬇ Download Template</button>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cHead}><div style={S.stepBadge(true)}>2</div><div><div style={S.cardTitle}>Upload CSV</div><div style={S.cardSub}>Max 500 rows per upload</div></div></div>
          <div style={S.cBody}>
            <div
              style={{ border: `2px dashed ${drag ? '#3b82f6' : file ? '#10b981' : '#cbd5e1'}`, borderRadius: 12, padding: 36, textAlign: 'center', cursor: 'pointer', background: drag ? '#eff6ff' : '#fafafa', transition: 'all .2s' }}
              onClick={() => document.getElementById('csv-bulk-input').click()}
              onDragOver={e => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
            >
              <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>{file ? file.name : 'Click or drag & drop CSV here'}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{file ? `${(file.size/1024).toFixed(1)} KB` : '.csv files only'}</div>
              <input id="csv-bulk-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFile} />
            </div>
          </div>
        </div>

        {preview.length > 0 && (
          <div style={{ ...S.card, overflow: 'hidden' }}>
            <div style={{ ...S.cHead, borderBottom: '1px solid #f1f5f9' }}><div style={S.stepBadge(true)}>3</div><div><div style={S.cardTitle}>Preview (first 5 rows)</div></div></div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{Object.keys(preview[0]).map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#475569', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
                <tbody>{preview.map((row, i) => <tr key={i}>{Object.values(row).map((v, j) => <td key={j} style={{ padding: '10px 14px', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>{v}</td>)}</tr>)}</tbody>
              </table>
            </div>
            <div style={S.cFoot}>
              <button style={{ ...S.btnPrimary, opacity: importing ? .7 : 1 }} onClick={handleImport} disabled={importing}>
                {importing ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Importing…</> : '⬆ Start Import'}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div style={S.card}>
            <div style={{ ...S.cHead }}><CheckCircle size={18} color="#10b981" /><div style={S.cardTitle}>Import Complete</div></div>
            <div style={{ ...S.cBody, display: 'flex', gap: 16 }}>
              {[{ label:'Successful', val: result.success, color:'#10b981' }, { label:'Failed', val: result.failed, color:'#ef4444' }, { label:'Total', val: result.total, color:'#3b82f6' }].map(s => (
                <div key={s.label} style={{ flex: 1, padding: 16, borderRadius: 12, borderLeft: `4px solid ${s.color}`, background: '#f8fafc' }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.val}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// USER PERMISSIONS PAGE  (unchanged)
// ─────────────────────────────────────────────────────────────────
const ALL_PERMISSIONS = [
  { key:'read',            label:'Read',            desc:'View data and reports' },
  { key:'write',           label:'Write',           desc:'Create and edit records' },
  { key:'delete',          label:'Delete',          desc:'Remove records permanently' },
  { key:'manage_users',    label:'Manage Users',    desc:'Add, edit, block users' },
  { key:'manage_projects', label:'Manage Projects', desc:'Create and assign projects' },
  { key:'manage_wallet',   label:'Manage Wallet',   desc:'Credit/debit wallet balances' },
  { key:'manage_teams',    label:'Manage Teams',    desc:'Create and manage teams' },
];

export function UserPermissionsPage() {
  const [userId, setUserId] = useState('');
  const [perms,  setPerms]  = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const toggle = p => { setPerms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]); setSaved(false); };
  const save = async () => {
    if (!userId.trim()) return;
    setSaving(true);
    await userController.updatePermissions(userId.trim(), perms);
    setSaved(true); setSaving(false);
  };
  return (
    <div style={S.page}>
      <div style={{ marginBottom: 24 }}><h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>User Permissions</h1><p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Override default role permissions for a specific user</p></div>
      <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={S.card}>
          <div style={S.cHead}><div style={S.stepBadge(true)}>1</div><div><div style={S.cardTitle}>Select User</div><div style={S.cardSub}>Enter user ID or custom ID (e.g. RTL-00042)</div></div></div>
          <div style={S.cBody}><Input label="User ID" value={userId} onChange={e => { setUserId(e.target.value); setSaved(false); }} placeholder="RTL-00042 or MongoDB ObjectId…" /></div>
        </div>
        <div style={S.card}>
          <div style={S.cHead}><div style={S.stepBadge(true)}>2</div><div><div style={S.cardTitle}>Set Permissions</div><div style={S.cardSub}>Toggle individual permissions</div></div></div>
          <div style={S.cBody}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ALL_PERMISSIONS.map(p => {
                const active = perms.includes(p.key);
                return (
                  <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${active ? '#3b82f6' : '#e2e8f0'}`, background: active ? '#eff6ff' : '#fafafa', transition: 'all .15s' }}>
                    <div onClick={() => toggle(p.key)} style={{ width: 38, height: 22, borderRadius: 11, background: active ? '#3b82f6' : '#e2e8f0', position: 'relative', transition: 'background .2s', cursor: 'pointer', flexShrink: 0 }}>
                      <div style={{ position: 'absolute', top: 3, left: active ? 18 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left .2s' }} />
                    </div>
                    <div><div style={{ fontSize: 13, fontWeight: 600, color: active ? '#1e40af' : '#0f172a' }}>{p.label}</div><div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{p.desc}</div></div>
                    <input type="checkbox" checked={active} onChange={() => toggle(p.key)} style={{ display: 'none' }} />
                  </label>
                );
              })}
            </div>
          </div>
          <div style={S.cFoot}>
            {saved && <span style={{ fontSize: 13, color: '#10b981', fontWeight: 500 }}>✓ Saved</span>}
            <button style={S.btnSecond} onClick={() => { setPerms([]); setSaved(false); }}>Clear All</button>
            <button style={{ ...S.btnPrimary, opacity: (saving || !userId.trim()) ? .6 : 1 }} onClick={save} disabled={saving || !userId.trim()}>
              {saving ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</> : 'Save Permissions'}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// USER ROLES PAGE  (unchanged)
// ─────────────────────────────────────────────────────────────────
export function UserRolesPage() {
  const roles = [
    { role:'admin',             label:'Admin',            desc:'Full platform access. Manages all distributors, settings and company data.', perms:['read','write','delete','manage_users','manage_projects','manage_wallet','manage_teams'] },
    { role:'super_distributor', label:'Super Distributor',desc:'Manages multiple distributors. Can credit wallets.',                        perms:['read','write','manage_wallet'] },
    { role:'distributor',       label:'Distributor',      desc:'Manages sub-distributors and retailers within their territory.',             perms:['read','write'] },
    { role:'subdistributor',    label:'Sub Distributor',  desc:'Optional level. Manages retailers in a sub-territory.',                     perms:['read','write'] },
    { role:'retailer',          label:'Retailer',         desc:'Customer-facing role. Handles day-to-day orders.',                          perms:['read'] },
    { role:'employee',          label:'Employee',         desc:'Staff under any node. Can be under SD, Dist, SubDist or Retailer.',         perms:['read'] },
  ];
  return (
    <div style={S.page}>
      <div style={{ marginBottom: 24 }}><h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>User Roles</h1><p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Hierarchy overview and default permissions per role</p></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginBottom: 24 }}>
        {roles.map((r, i) => (
          <div key={r.role} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {i > 0 && <span style={{ color: '#cbd5e1', fontSize: 12 }}>→</span>}
            <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: `${ROLE_COLORS[r.role]}15`, color: ROLE_COLORS[r.role], border: `1.5px solid ${ROLE_COLORS[r.role]}40` }}>
              {ROLE_ICONS[r.role]} {r.label}
            </span>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
        {roles.map(r => (
          <div key={r.role} style={{ ...S.card, overflow: 'hidden' }}>
            <div style={{ height: 4, background: ROLE_COLORS[r.role] }} />
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: `${ROLE_COLORS[r.role]}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{ROLE_ICONS[r.role]}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{r.label}</div>
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: `${ROLE_COLORS[r.role]}15`, color: ROLE_COLORS[r.role] }}>{r.role}</span>
                </div>
              </div>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px', lineHeight: 1.5 }}>{r.desc}</p>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 8 }}>Default Permissions</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {r.perms.map(p => <span key={p} style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, background: '#f1f5f9', color: '#475569' }}>{p.replace(/_/g,' ')}</span>)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}